import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  generateWallet,
  getWallet,
  clearWallet,
  shortFingerprint,
} from "../lib/wallet";
import { listDraftsByWallet, deleteDraft } from "../lib/api";
import { toast } from "sonner";

const notifyWalletChange = () => {
  window.dispatchEvent(new Event("cierge-wallet-changed"));
};

const WalletIntro = ({ onCreate, generating }) => {
  const [handle, setHandle] = useState("");
  return (
    <section className="border-b border-border">
      <div className="mx-auto grid max-w-[1280px] gap-12 px-6 py-20 md:grid-cols-12 md:py-24 lg:px-10">
        <div className="md:col-span-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
            Optional · self-sovereign wallet
          </p>
          <h1 className="mt-4 font-serif text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl">
            A wallet you actually hold.
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground">
            Your wallet is generated in this browser. The private key never
            leaves it. KATLAS only ever sees a public-key fingerprint, and
            only when you choose to bind a draft. The wallet is for keeping
            continuity of your shaping work — not for storing broad
            documents.
          </p>
          <ul className="mt-8 space-y-3 font-mono text-[11px] uppercase tracking-[0.18em] text-foreground">
            <li>› ECDSA · P-256 keypair, generated client-side</li>
            <li>› Held in browser local storage</li>
            <li>› Only the fingerprint travels</li>
            <li>› You may clear it at any time</li>
          </ul>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onCreate(handle);
            }}
            className="mt-10 max-w-md"
          >
            <Label
              htmlFor="wallet-handle"
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
            >
              Optional handle
            </Label>
            <Input
              id="wallet-handle"
              data-testid="wallet-handle-input"
              placeholder="e.g. operator-2419"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              className="mt-2 rounded-none border-foreground/30 bg-white font-mono text-sm"
            />
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              Pseudonymous. Visible only to you.
            </p>
            <Button
              type="submit"
              data-testid="wallet-create-cta"
              disabled={generating}
              className="mt-6 rounded-none px-6 font-mono text-xs uppercase tracking-[0.18em]"
            >
              {generating ? "Generating keypair…" : "Generate keypair →"}
            </Button>
          </form>
        </div>
        <div className="md:col-span-6">
          <div className="relative h-full min-h-[320px] w-full overflow-hidden border border-border bg-[#F5F5F5]">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/01f170b3-4363-4afb-9d08-9675f5c35db5/images/2db04b2f252ec5949719431d682fbfa77661f3583ecf0acd0b840bca90444587.png"
              alt="Visual reference for self-sovereign custody"
              className="h-full w-full object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 border-t border-border bg-white/95 px-5 py-4 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              <div className="flex items-center justify-between">
                <span>Custody · user-held</span>
                <span>Not a document vault</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const WalletDashboard = ({ wallet, onClear }) => {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadDrafts = async () => {
    setLoading(true);
    try {
      const data = await listDraftsByWallet(wallet.fingerprint);
      setDrafts(data || []);
    } catch (e) {
      // backend may be unavailable
      setDrafts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDrafts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id) => {
    try {
      await deleteDraft(id);
      setDrafts((d) => d.filter((x) => x.id !== id));
      toast.success("Draft removed.");
    } catch {
      toast.error("Could not remove draft.");
    }
  };

  return (
    <>
      <section className="border-b border-border bg-[#FAFAFA]">
        <div className="mx-auto max-w-[1280px] px-6 py-14 lg:px-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
            Wallet · active in this browser
          </p>
          <div className="mt-4 grid gap-8 md:grid-cols-12">
            <div className="md:col-span-8">
              <h1 className="font-serif text-4xl font-light leading-tight tracking-tight sm:text-5xl">
                {wallet.handle}
              </h1>
              <p
                data-testid="wallet-fingerprint"
                className="mt-4 break-all font-mono text-xs tracking-[0.04em] text-foreground"
              >
                {wallet.fingerprint}
              </p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                key fingerprint · sha-256 over public jwk
              </p>
            </div>
            <div className="md:col-span-4">
              <div className="border border-border bg-white p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  Created
                </p>
                <p className="mt-2 font-mono text-sm">
                  {new Date(wallet.createdAt).toLocaleString()}
                </p>
                <Button
                  type="button"
                  data-testid="wallet-clear-cta"
                  variant="outline"
                  onClick={onClear}
                  className="mt-6 w-full rounded-none border-destructive/40 bg-white font-mono text-[11px] uppercase tracking-[0.18em] text-destructive hover:border-destructive hover:text-destructive"
                >
                  Clear wallet
                </Button>
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  Removes keypair from this browser. Cannot be recovered.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto max-w-[1280px] px-6 py-16 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
                Saved drafts
              </p>
              <h2 className="mt-3 font-serif text-3xl font-light tracking-tight">
                Use cases bound to this wallet.
              </h2>
            </div>
            <Button
              asChild
              className="rounded-none font-mono text-xs uppercase tracking-[0.18em]"
              data-testid="wallet-shape-new"
            >
              <Link to="/explore">Shape a new use case →</Link>
            </Button>
          </div>

          <div className="mt-10">
            {loading ? (
              <p
                data-testid="wallet-drafts-loading"
                className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground"
              >
                Loading saved drafts…
              </p>
            ) : drafts.length === 0 ? (
              <div
                data-testid="wallet-drafts-empty"
                className="border border-dashed border-border bg-white p-10 text-center"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  No drafts yet
                </p>
                <p className="mt-3 font-serif text-xl font-light tracking-tight">
                  Shape your first governed use case.
                </p>
              </div>
            ) : (
              <ul
                data-testid="wallet-drafts-list"
                className="grid gap-px bg-border md:grid-cols-2"
              >
                {drafts.map((d) => (
                  <li
                    key={d.id}
                    data-testid={`wallet-draft-${d.id}`}
                    className="bg-white p-7"
                  >
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      {new Date(d.created_at).toLocaleString()}
                    </p>
                    <h3 className="mt-3 font-serif text-2xl font-light tracking-tight">
                      {d.headline}
                    </h3>
                    <ul className="mt-4 space-y-1 font-mono text-[11px] uppercase tracking-[0.16em] text-foreground/80">
                      <li>· {d.user_type?.label}</li>
                      <li>· {d.sector?.label}</li>
                      <li>· {d.problem?.label}</li>
                      <li>· {d.scope?.label}</li>
                    </ul>
                    <button
                      type="button"
                      data-testid={`wallet-draft-delete-${d.id}`}
                      onClick={() => handleDelete(d.id)}
                      className="mt-6 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-destructive"
                    >
                      Remove draft
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export const Wallet = () => {
  const [wallet, setWallet] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    setWallet(getWallet());
  }, []);

  const handleCreate = async (handle) => {
    setGenerating(true);
    try {
      const w = await generateWallet(handle);
      setWallet(w);
      notifyWalletChange();
      toast.success("Wallet created. Held in this browser only.");
    } catch (e) {
      toast.error("Could not generate wallet in this browser.");
    } finally {
      setGenerating(false);
    }
  };

  const handleClear = () => {
    clearWallet();
    setWallet(null);
    notifyWalletChange();
    toast("Wallet cleared from this browser.");
  };

  return (
    <div data-testid="wallet-page" className="bg-white">
      {wallet ? (
        <WalletDashboard wallet={wallet} onClear={handleClear} />
      ) : (
        <WalletIntro onCreate={handleCreate} generating={generating} />
      )}
    </div>
  );
};

export default Wallet;
